import React from 'react';
import { useRecoilValue } from 'recoil';
import { Button, TooltipDisclaimer } from '@ui-elements';
import { OfferContent } from '~/common/models';
import {
  ActiveSpecialOffer,
  OfferContentFeatures,
  OfferContentTitleDisclaimer,
} from '../../../special-offer/store';
import { resolveOfferCta } from '../../cta-template';
import { ENQUIRY_ANCHOR_ID, PageEnquiryForm } from '../../store';
import { OfferSecondaryCtas } from '../offer-secondary-ctas';
import styles from './index.module.scss';

/**
 * The offer copy, as a single centred column.
 *
 * The redesign drops the blue side panel that the existing page puts beside the copy, so the heading, the
 * body and the calls to action all sit in one measure. Everything shown is authored: the only thing this
 * component decides is where the primary button goes, which depends on whether an enquiry form was resolved
 * for the offer.
 */
export const OfferContentV2: React.FC = () => {
  const offer = useRecoilValue(ActiveSpecialOffer);
  const features = useRecoilValue(OfferContentFeatures);
  const titleDisclaimer = useRecoilValue(OfferContentTitleDisclaimer);
  const enquiryForm = useRecoilValue(PageEnquiryForm);

  if (!offer) return null;

  const heading = (
    <h1
      className={styles.heading}
      data-testid="offer-heading"
      // Authored in the backoffice's rich text editor, as on the existing page.
      dangerouslySetInnerHTML={{ __html: unwrapTextNode(offer.contentTitle) }}
    />
  );

  return (
    <div className={styles.content} data-testid="offer-content-v2">
      {/* The existing page shows the heading only when a title disclaimer exists, which silently hides the
          title of every offer without one. Here the heading always renders and the tooltip is what is
          conditional. */}
      {titleDisclaimer?.contentType === 'DISCLAIMER-CONTENT-TITLE' ? (
        <TooltipDisclaimer
          customContent
          disclaimer={titleDisclaimer.content}
          triggerClassName={styles.disclaimerTrigger}
        >
          {heading}
        </TooltipDisclaimer>
      ) : (
        heading
      )}

      {features.map((content, index) => (
        <OfferBody key={`FEATURE-${index}`} {...content} />
      ))}

      {/* The only button on this page. Everything else the offer carries is an icon link in the row below,
          as the design has it - see OfferCtaLinks for why the stored sections do not decide that here. */}
      <div className={styles.enquireRow}>
        <EnquiryButton
          offerUniqueName={offer.uniqueName}
          hasFormOnPage={!!enquiryForm}
        />
      </div>

      <OfferSecondaryCtas />
    </div>
  );
};

/**
 * The enquiry button.
 *
 * With a form on the page it scrolls to it rather than navigating, which is the reason the redesign exists.
 * Without one - no form on the offer and no default - it falls back to the separate enquiry page, so an
 * offer is never left without a way to enquire.
 *
 * It stays a real link to the anchor, so middle-click, "open in new tab" and a keyboard Enter all still do
 * something sensible, and the page still works if the click handler never runs. The handler only takes over
 * to animate the trip, which a bare anchor jumps in one frame.
 */
const EnquiryButton: React.FC<{
  offerUniqueName: string;
  hasFormOnPage: boolean;
}> = ({ offerUniqueName, hasFormOnPage }) => {
  const scrollToForm = (event: React.MouseEvent<HTMLElement>) => {
    if (!hasFormOnPage) return;

    const form = document.getElementById(ENQUIRY_ANCHOR_ID);
    if (!form) return;

    event.preventDefault();

    // Honour the reader's own setting: an animated scroll is exactly the kind of motion people turn off.
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    form.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  return (
    <Button
      buttonSize="large"
      buttonWidth="full-mobile-only"
      className={styles.primaryCta}
      href={
        hasFormOnPage ? `#${ENQUIRY_ANCHOR_ID}` : `${offerUniqueName}/enquire`
      }
      onClick={scrollToForm}
      type="primary"
      data-testid="offer-enquire-button"
    >
      Enquire now
    </Button>
  );
};

/** Paragraph and list content, in the redesign's centred measure. */
const OfferBody = (content: OfferContent) => {
  if (content.contentType === 'PARAGRAPH') {
    return (
      <div
        className={styles.paragraph}
        dangerouslySetInnerHTML={{ __html: content.content }}
      />
    );
  }

  if (content.contentType === 'LIST') {
    return (
      <ul className={styles.list}>
        {content.content.map((item, index) => (
          <li key={index}>
            <div dangerouslySetInnerHTML={{ __html: item }} />
          </li>
        ))}
      </ul>
    );
  }

  return null;
};

/**
 * Strips the editor's wrapper paragraph so the heading is not a block inside a heading. Same treatment the
 * existing page applies.
 */
function unwrapTextNode(html: string): string {
  return html.replace(/<p class=["']text-node["']>(.*?)<\/p>/gi, '$1').trim();
}
